<!-- FRC-749: ≤1024px only. The desktop sidebar owns ≥1025px (investor-dashboard.component.html). -->
<div class="min-[1025px]:tw-hidden">
  <!-- In-flow spacer: keeps page content and bottom CTAs clear of the fixed bar (+ safe area). -->
  <div class="investor-bottom-nav__spacer" aria-hidden="true"></div>

  <nav
    class="investor-bottom-nav__bar tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-sticky tw-flex tw-items-start tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-bg-white tw-px-2 tw-pt-2 tw-font-body"
    aria-label="investor dashboard"
  >
    <!-- While a close finishes, taps land on the (inert) white <nav> instead of passing through. -->
    <ul
      class="tw-m-0 tw-flex tw-w-full tw-list-none tw-p-0"
      [class.tw-pointer-events-none]="isClosing"
    >
      @for (tab of visibleTabs; track tab.name) {
        <li class="tw-flex tw-min-w-0 tw-flex-1">
          @if (isGroup(tab)) {
            <button
              type="button"
              class="investor-bottom-nav__tab"
              aria-haspopup="dialog"
              [attr.aria-expanded]="openGroup === tab"
              (click)="onGroupTabClick(tab, $any($event.currentTarget))"
            >
              <ng-container *ngTemplateOutlet="tabContent; context: { tab: tab }" />
            </button>
          } @else if (tab.isExternal) {
            <a class="investor-bottom-nav__tab" [href]="tab.url">
              <ng-container *ngTemplateOutlet="tabContent; context: { tab: tab }" />
            </a>
          } @else {
            <a
              class="investor-bottom-nav__tab"
              [routerLink]="tab.url"
              [replaceUrl]="!!openGroup"
              [attr.aria-current]="isTabActive(tab) ? 'page' : null"
              (click)="onDirectTabClick($event, tab)"
            >
              <ng-container *ngTemplateOutlet="tabContent; context: { tab: tab }" />
            </a>
          }
        </li>
      }
    </ul>
  </nav>

  @if (openGroup) {
    <!-- Ends at the bar's top edge so the bar stays fixed and usable while a sheet is open (Figma). -->
    <div class="investor-bottom-nav__overlay tw-fixed tw-inset-x-0 tw-top-0 tw-z-modal">
      <!-- Scrim: tap to close. Not focusable — Esc and the X cover keyboard users; the Esc binding
           here just mirrors the sheet's so the click has a keyboard equivalent. -->
      <div
        class="tw-absolute tw-inset-0 tw-bg-black/[0.45]"
        (click)="dismissSheet()"
        (keydown.escape)="dismissSheet()"
      ></div>

      <div
        class="investor-bottom-nav__sheet tw-absolute tw-inset-x-0 tw-bottom-0 tw-mx-auto tw-flex tw-w-full tw-max-w-[600px] tw-flex-col tw-gap-1 tw-overflow-hidden tw-rounded-t-[20px] tw-bg-white tw-px-4 tw-pt-2 tw-font-body"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="sheetTitleId"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        (keydown.escape)="dismissSheet()"
      >
        <div class="tw-flex tw-shrink-0 tw-justify-center tw-pb-1" aria-hidden="true">
          <div class="tw-h-1 tw-w-10 tw-rounded-[2px] tw-bg-gray-300"></div>
        </div>

        <div
          class="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-pb-1.5 tw-pl-3 tw-pr-1"
        >
          <!-- Initial focus lands on the title (not the X) so no focus ring flashes on open. -->
          <h2
            class="tw-m-0 tw-text-[20px] tw-font-bold tw-leading-[1.3] tw-text-gray-800 focus:tw-outline-none"
            [id]="sheetTitleId"
            tabindex="-1"
            cdkFocusInitial
          >
            {{ openGroup.name | translate }}
          </h2>
          <!-- 44×44 hit area around the 32×32 Figma close glyph. -->
          <button
            type="button"
            class="-tw-my-1.5 -tw-mr-1.5 tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-primary"
            [attr.aria-label]="'common.close' | translate"
            (click)="dismissSheet()"
          >
            <img
              class="tw-block"
              src="assets/img/icons/investor-sidemenu/close.svg"
              width="32"
              height="32"
              alt=""
            />
          </button>
        </div>

        <ul
          class="tw-m-0 tw-flex tw-min-h-0 tw-flex-1 tw-list-none tw-flex-col tw-gap-1 tw-overflow-y-auto tw-overflow-x-hidden tw-p-0"
        >
          @for (item of visibleItems(openGroup); track item.name + item.url) {
            <li>
              @if (item.action) {
                <button
                  type="button"
                  class="investor-bottom-nav__item tw-border-0 tw-bg-transparent tw-text-left"
                  (click)="onItemClick($event, item)"
                >
                  <ng-container *ngTemplateOutlet="itemContent; context: { item: item }" />
                </button>
              } @else if (item.isExternal) {
                <a
                  class="investor-bottom-nav__item"
                  [href]="item.url"
                  (click)="onItemClick($event, item)"
                >
                  <ng-container *ngTemplateOutlet="itemContent; context: { item: item }" />
                </a>
              } @else {
                <a
                  class="investor-bottom-nav__item"
                  [routerLink]="item.url"
                  [replaceUrl]="true"
                  [class.tw-bg-divider-light]="isItemSelected(item)"
                  [attr.aria-current]="isItemSelected(item) ? 'page' : null"
                  (click)="onItemClick($event, item)"
                >
                  <ng-container *ngTemplateOutlet="itemContent; context: { item: item }" />
                </a>
              }
            </li>
          }
        </ul>
      </div>
    </div>
  }
</div>

<ng-template #tabContent let-tab="tab">
  @let active = isTabActive(tab);
  @let badge = badgeFor(tab);
  <span
    class="tw-relative tw-flex tw-h-[30px] tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[15px]"
    [class.tw-bg-divider-light]="active"
  >
    @if (isProfileTab(tab)) {
      <img
        class="tw-block tw-h-5 tw-w-5 tw-rounded-full tw-object-cover"
        [src]="profileImage"
        width="20"
        height="20"
        alt=""
      />
    } @else if (tab.mobileIconSrc || tab.iconSrc) {
      <!-- No width/height override: each SVG carries its Figma size (Token's coins is 18px). -->
      <img class="tw-block" [src]="tab.mobileIconSrc || tab.iconSrc" alt="" />
    }
    @if (badge) {
      <span
        class="tw-absolute -tw-top-1 tw-right-1 tw-flex tw-h-[18px] tw-min-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-1 tw-text-[11px] tw-font-bold tw-leading-none tw-text-white"
        aria-hidden="true"
      >
        {{ badge }}
      </span>
    }
  </span>
  <span class="tw-flex tw-max-w-full tw-items-center tw-gap-0.5">
    <span
      class="tw-truncate tw-text-xs tw-leading-[1.4]"
      [class.tw-font-bold]="active"
      [class.tw-text-gray-800]="active"
      [class.tw-font-medium]="!active"
      [class.tw-text-body]="!active"
    >
      {{ tab.name | translate }}
    </span>
    @if (isGroup(tab)) {
      <img
        class="tw-block tw-shrink-0"
        [src]="
          active
            ? 'assets/img/icons/investor-sidemenu/chevron-sm-active.svg'
            : 'assets/img/icons/investor-sidemenu/chevron-sm.svg'
        "
        width="12"
        height="12"
        alt=""
      />
    }
  </span>
  @if (badge) {
    <span class="tw-sr-only">{{
      'dashboard.sidemenu.todoBadgeLabel' | translate: { count: badge }
    }}</span>
  }
</ng-template>

<ng-template #itemContent let-item="item">
  <span class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
    <span class="tw-text-base tw-font-bold tw-leading-[1.5] tw-text-gray-800">
      {{ item.name | translate }}
    </span>
    @if (item.description) {
      <span class="tw-text-sm tw-font-normal tw-leading-[1.6] tw-text-divider-dark">
        {{ item.description | translate }}
      </span>
    }
  </span>
</ng-template>
